import React, { useState, useEffect } from 'react';
import DashboardLayout from './DashboardLayout';
import { getUsers, createUser, updateUser, deleteUser, getBranches } from '../lib/api';
import { getRoleLabel, formatCurrency } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Plus,
  Pencil,
  Trash2,
  Loader2,
  User,
  KeyRound,
  DollarSign,
} from 'lucide-react';
import { toast } from 'sonner';

const ROLES = [
  { value: 'admin', label: 'Administrador' },
  { value: 'gerente', label: 'Gerente' },
  { value: 'cajero', label: 'Cajero' },
  { value: 'operador', label: 'Operador' },
];

const DAYS = [
  { value: 'lunes', label: 'Lunes' },
  { value: 'martes', label: 'Martes' },
  { value: 'miercoles', label: 'Miércoles' },
  { value: 'jueves', label: 'Jueves' },
  { value: 'viernes', label: 'Viernes' },
  { value: 'sabado', label: 'Sábado' },
  { value: 'domingo', label: 'Domingo' },
];

const SCHEDULES = [
  { value: '8:30-15:30', label: '8:30 AM - 3:30 PM' },
  { value: '9:00-17:00', label: '9:00 AM - 5:00 PM' },
  { value: '10:00-18:00', label: '10:00 AM - 6:00 PM' },
];

const emptyForm = {
  name: '',
  pin: '',
  role: 'cajero',
  salary: '',
  bonus: '',
  schedule: '',
  rest_day: '',
  branch_id: '',
};

export default function AdminUsersPage() {
  const [users, setUsers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [usersRes, branchesRes] = await Promise.all([getUsers(), getBranches()]);
      setUsers(usersRes.data);
      setBranches(branchesRes.data);
    } catch (error) {
      toast.error('Error al cargar datos');
    }
    setLoading(false);
  };

  const handleOpenModal = (user = null) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        name: user.name,
        pin: user.pin || '',
        role: user.role,
        salary: user.salary?.toString() || '',
        bonus: user.bonus?.toString() || '',
        schedule: user.schedule || '',
        rest_day: user.rest_day || '',
        branch_id: user.branch_id || '',
      });
    } else {
      setEditingUser(null);
      setFormData(emptyForm);
    }
    setShowModal(true);
  };

  const handlePinChange = (value) => {
    // Only allow up to 4 numeric digits
    const cleaned = value.replace(/\D/g, '').slice(0, 4);
    setFormData({ ...formData, pin: cleaned });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!editingUser && formData.pin.length !== 4) {
      toast.error('El PIN debe ser de 4 dígitos');
      return;
    }
    if (editingUser && formData.pin && formData.pin.length !== 4) {
      toast.error('El PIN debe ser de 4 dígitos');
      return;
    }

    if (formData.role === 'cajero' && (!formData.branch_id || formData.branch_id === 'none')) {
      toast.error('Un cajero debe tener una sucursal asignada');
      return;
    }

    setSubmitting(true);
    try {
      const data = {
        name: formData.name,
        role: formData.role,
        salary: formData.salary ? parseFloat(formData.salary) : null,
        bonus: formData.bonus ? parseFloat(formData.bonus) : null,
        schedule: formData.schedule || null,
        rest_day: formData.rest_day || null,
        branch_id: formData.branch_id && formData.branch_id !== 'none' ? formData.branch_id : null,
      };
      if (editingUser) {
        if (formData.pin) data.pin = formData.pin;
        await updateUser(editingUser.id, data);
        toast.success('Usuario actualizado');
      } else {
        data.pin = formData.pin;
        await createUser(data);
        toast.success('Usuario creado');
      }
      setShowModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    }
    setSubmitting(false);
  };

  const handleDelete = async (user) => {
    if (!window.confirm(`¿Eliminar "${user.name}"?`)) return;
    try {
      await deleteUser(user.id);
      setUsers(users.filter((u) => u.id !== user.id));
      toast.success('Usuario eliminado');
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const getBranchName = (branchId) => {
    const branch = branches.find((b) => b.id === branchId);
    return branch?.name || '—';
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'admin':
        return 'bg-purple-100 text-purple-700 border-purple-200';
      case 'gerente':
        return 'bg-indigo-100 text-indigo-700 border-indigo-200';
      case 'cajero':
        return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'operador':
        return 'bg-emerald-100 text-emerald-700 border-emerald-200';
      default:
        return 'bg-slate-100 text-slate-700 border-slate-200';
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fadeIn">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Usuarios</h1>
            <p className="text-slate-600 mt-1">Administra los usuarios del sistema</p>
          </div>
          <Button onClick={() => handleOpenModal()} className="gap-2" data-testid="add-user-btn">
            <Plus className="w-5 h-5" />
            Nuevo Usuario
          </Button>
        </div>

        <div className="card-base overflow-hidden">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-slate-200">
                <th className="table-header">Usuario</th>
                <th className="table-header">PIN</th>
                <th className="table-header">Rol</th>
                <th className="table-header">Sueldo semanal</th>
                <th className="table-header">Sucursal</th>
                <th className="table-header text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id} className="table-row" data-testid={`user-row-${user.id}`}>
                  <td className="table-cell">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center">
                        <User className="w-5 h-5 text-slate-500" />
                      </div>
                      <div>
                        <span className="font-medium text-slate-900">{user.name}</span>
                        {user.rest_day && (
                          <div className="text-xs text-slate-400 capitalize">Desc: {user.rest_day}</div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="table-cell">
                    {user.pin ? (
                      <span className="inline-flex items-center gap-2 font-mono font-bold text-blue-700 bg-blue-50 border border-blue-200 px-2.5 py-1 rounded-lg tracking-widest">
                        <KeyRound className="w-3.5 h-3.5" />
                        {user.pin}
                      </span>
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                  </td>
                  <td className="table-cell">
                    <span className={`status-badge ${getRoleBadgeColor(user.role)}`}>
                      {getRoleLabel(user.role)}
                    </span>
                  </td>
                  <td className="table-cell">
                    {user.salary ? (
                      <div>
                        <div className="font-medium text-slate-900">{formatCurrency(user.salary)}</div>
                        {user.bonus > 0 && (
                          <div className="text-xs text-emerald-600">+{formatCurrency(user.bonus)} bono</div>
                        )}
                      </div>
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                  </td>
                  <td className="table-cell text-slate-600">{getBranchName(user.branch_id)}</td>
                  <td className="table-cell text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleOpenModal(user)}
                        data-testid={`edit-user-${user.id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-red-500 hover:text-red-700"
                        onClick={() => handleDelete(user)}
                        data-testid={`delete-user-${user.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingUser ? 'Editar Usuario' : 'Nuevo Usuario'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name">Nombre *</Label>
              <div className="relative mt-1">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Nombre completo"
                  required
                  className="pl-10"
                  data-testid="user-name-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="pin">
                PIN de 4 dígitos {editingUser ? '(dejar vacío para no cambiar)' : '*'}
              </Label>
              <div className="relative mt-1">
                <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="pin"
                  type="text"
                  inputMode="numeric"
                  pattern="\d{4}"
                  maxLength={4}
                  value={formData.pin}
                  onChange={(e) => handlePinChange(e.target.value)}
                  placeholder="0000"
                  required={!editingUser}
                  className="pl-10 font-mono tracking-widest text-lg"
                  data-testid="user-pin-input"
                />
              </div>
              <p className="text-xs text-slate-500 mt-1">
                Este PIN será usado para iniciar sesión. Debe ser único.
              </p>
            </div>

            <div>
              <Label>Rol *</Label>
              <Select
                value={formData.role}
                onValueChange={(value) => setFormData({ ...formData, role: value })}
              >
                <SelectTrigger className="mt-1" data-testid="user-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ROLES.map((role) => (
                    <SelectItem key={role.value} value={role.value}>
                      {role.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Salary & Bonus */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="salary">Sueldo semanal (MXN)</Label>
                <div className="relative mt-1">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="salary"
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.salary}
                    onChange={(e) => setFormData({ ...formData, salary: e.target.value })}
                    placeholder="0"
                    className="pl-10"
                    data-testid="user-salary-input"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="bonus">Bono (MXN)</Label>
                <div className="relative mt-1">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="bonus"
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.bonus}
                    onChange={(e) => setFormData({ ...formData, bonus: e.target.value })}
                    placeholder="0"
                    className="pl-10"
                    data-testid="user-bonus-input"
                  />
                </div>
              </div>
            </div>

            {/* Schedule & Rest Day (for operators) */}
            {formData.role === 'operador' && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Horario</Label>
                  <Select
                    value={formData.schedule}
                    onValueChange={(value) => setFormData({ ...formData, schedule: value })}
                  >
                    <SelectTrigger className="mt-1" data-testid="user-schedule-select">
                      <SelectValue placeholder="Seleccionar..." />
                    </SelectTrigger>
                    <SelectContent>
                      {SCHEDULES.map((s) => (
                        <SelectItem key={s.value} value={s.value}>
                          {s.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Día de descanso</Label>
                  <Select
                    value={formData.rest_day}
                    onValueChange={(value) => setFormData({ ...formData, rest_day: value })}
                  >
                    <SelectTrigger className="mt-1" data-testid="user-restday-select">
                      <SelectValue placeholder="Seleccionar..." />
                    </SelectTrigger>
                    <SelectContent>
                      {DAYS.map((d) => (
                        <SelectItem key={d.value} value={d.value}>
                          {d.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {/* Branch — required for cajero */}
            {branches.length > 0 && (
              <div>
                <Label>
                  Sucursal {formData.role === 'cajero' && <span className="text-red-500">*</span>}
                </Label>
                <Select
                  value={formData.branch_id || 'none'}
                  onValueChange={(value) => setFormData({ ...formData, branch_id: value })}
                >
                  <SelectTrigger className="mt-1" data-testid="user-branch-select">
                    <SelectValue placeholder="Sin asignar" />
                  </SelectTrigger>
                  <SelectContent>
                    {formData.role !== 'cajero' && <SelectItem value="none">Sin asignar</SelectItem>}
                    {branches.map((b) => (
                      <SelectItem key={b.id} value={b.id}>
                        {b.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {formData.role === 'cajero' && (
                  <p className="text-xs text-slate-500 mt-1">
                    El cajero solo podrá operar POS y caja chica de esta sucursal.
                  </p>
                )}
              </div>
            )}

            <Button
              type="submit"
              className="w-full"
              disabled={submitting}
              data-testid="save-user-btn"
            >
              {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Guardar'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
